import type {
  DesktopWidget,
  GetAllDesktopSettingsData
} from 'shared/api/containerApi'
import type { WidgetConfiguration } from 'shared/api/containerApi/requests/getWidgets/getWidgets.types'
import { WidgetKey, WidgetSize } from 'shared/constants/widgets'
import type {
  AiArtifactConfig,
  PersistedWidgetSpec
} from 'shared/types/widgets'
import { dedupePersistedWidgetSpecs } from 'shared/utils/dedupeWidgets'

const widgetKeys = new Set<string>(Object.values(WidgetKey))
const widgetSizes = new Set<string>(Object.values(WidgetSize))

const readWidgetConfig = (
  widget: DesktopWidget,
  widgetKey: WidgetKey
): PersistedWidgetSpec['config'] => {
  if (widgetKey === WidgetKey.CLOCK) {
    const { primaryTimezone, secondaryTimezones } = widget.configuration ?? {}
    if (
      typeof primaryTimezone === 'string' &&
      Array.isArray(secondaryTimezones) &&
      secondaryTimezones.every(timezone => typeof timezone === 'string')
    ) {
      return { primaryTimezone, secondaryTimezones }
    }
  }

  if (widgetKey === WidgetKey.AI_ARTIFACT_TILE) {
    const publicId = widget.configuration?.publicId
    if (typeof publicId === 'string' && publicId) {
      return { publicId } as AiArtifactConfig
    }
  }

  return undefined
}

export const apiWidgetToPersistedSpec = (
  widget: DesktopWidget
): PersistedWidgetSpec | null => {
  if (!widgetKeys.has(widget.widget_key) || !widgetSizes.has(widget.size)) {
    return null
  }

  const widgetKey = widget.widget_key as WidgetKey
  const config = readWidgetConfig(widget, widgetKey)

  return {
    id: widget.id,
    widgetKey,
    size: widget.size as WidgetSize,
    displayOrder: widget.display_order,
    isCollapsed: Boolean(widget.is_collapsed),
    isForced: Boolean(widget.is_forced),
    ...(config ? { config } : {})
  }
}

export const persistedSpecToApiWidget = (
  widget: PersistedWidgetSpec
): DesktopWidget => ({
  id: widget.id,
  widget_key: widget.widgetKey,
  configuration: (widget.config ?? {}) as WidgetConfiguration,
  size: widget.size,
  display_order: widget.displayOrder,
  is_collapsed: widget.isCollapsed ?? false,
  is_forced: widget.isForced ?? false
})

export const selectDesktopWidgets = (
  data: GetAllDesktopSettingsData | undefined
): PersistedWidgetSpec[] => {
  if (!data) return []

  const widgets = [...data.widgets]
    .sort(
      (a, b) =>
        a.display_order - b.display_order ||
        (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)
    )
    .flatMap(widget => {
      const spec = apiWidgetToPersistedSpec(widget)
      return spec ? [spec] : []
    })

  return dedupePersistedWidgetSpecs(widgets)
}

/** Full order saves require every stored row to be visible in the UI. */
export const canSaveWidgetOrder = (
  data: GetAllDesktopSettingsData | undefined
): boolean =>
  Boolean(data && selectDesktopWidgets(data).length === data.widgets.length)
